import { onAuthStateChanged } from "firebase/auth";
import { useSyncExternalStore } from "react";
import { firebaseDienste } from "./firebase";
import { jagdtagLaeuft } from "./offline";
import { idToken } from "./sitzung";
import { abweichendeVersionBeobachten, VERSION, versionskoepfe } from "./version";
import { beobachten as warteschlangeBeobachten, uebertraegt } from "./warteschlange";

/**
 * Lebenszyklus der App (ADR-0041 §2–§5, Vertrag oberflaeche.md V-27, `oberflaeche/12`).
 *
 * - **Erkennen (§2):** Die App fragt `GET /api/version` beim Start, nach der Anmeldung, bei jeder Rückkehr (Vordergrund,
 *   wieder Netz), alle 30 Minuten Gerätezeit, solange sie sichtbar ist, und wenn eine Antwort des Dienstes eine andere
 *   Version nennt. Sie reagiert auf eine *andere* Version, nicht nur auf eine neuere: Ein Zurückrollen kommt genauso an.
 * - **Übernehmen (§3):** Der neue Service Worker installiert sich im Hintergrund und wartet (sw.ts). Übernommen wird nur an
 *   einem sicheren Punkt: keine Texteingabe, kein (modaler) Dialog, keine Übertragung der Warteschlange unterwegs (die
 *   Warteschlange liegt dauerhaft in IndexedDB), und die App ist innerhalb von 10 Sekunden nach dem Start bzw. einer
 *   Rückkehr, geht gerade in den Hintergrund oder wurde 30 Minuten nicht bedient; im Hintergrund prüft und wechselt sie
 *   sonst nicht (dort ruht eine App auf dem Telefon). Nach dem Neuladen zeigt sie einmal
 *   „Neue Version geladen.“; die Sitzung bleibt.
 * - **Jagdtag (§4):** Ab „Offline bereit“ bis zum Ende der Drückjagd (letzter Jagdabschnitt) wartet die App, außer das
 *   Release ist dringend oder die App zu alt.
 * - **Zu alt (§5):** Nimmt der Dienst die Version nicht mehr an (`angenommen: false`), lädt die App sofort neu und zeigt bis
 *   dahin „Neue Version wird geladen.“; eine laufende Übertragung der Warteschlange wartet sie ab.
 */
export const WORTLAUT_NEUE_VERSION_GELADEN = "Neue Version geladen.";
export const WORTLAUT_NEUE_VERSION_WIRD_GELADEN = "Neue Version wird geladen.";

export const PRUEFTAKT_MS = 30 * 60_000;
export const FRISCH_MS = 10_000;
export const RUHE_MS = 30 * 60_000;
const TAKT_MS = 5_000;
const HINWEIS_GELADEN_MS = 10_000;
/** So lange steht „Neue Version wird geladen.“ mindestens, damit es zu lesen ist */
const HINWEIS_LADEN_MINDESTENS_MS = 1_500;
const ANTWORT_ABSTAND_MS = 5_000;
const INSTALLATION_MS = 20_000;
/**
 * Kommt nach der Aufforderung kein Wechsel des Workers, lädt die App trotzdem neu (der Browser aktiviert den wartenden
 * Worker dann für die Navigation). Hängt diese Navigation, weil die Aktivierung nicht vorankommt (CI-Pipeline 77982:
 * 20 Minuten), bricht die noch laufende alte Seite sie ab, meldet den Worker ab und lädt aus dem Netz; der Start
 * registriert ihn neu.
 */
const RUECKFALL_NEULADEN_MS = 4_000;
const RUECKFALL_ABMELDEN_MS = 8_000;
/** Höchstens so viele Versuche je Ziel, falls der Dienst eine Version meldet, die er (noch) nicht ausliefert */
const VERSUCHE_JE_ZIEL = 2;
const MERKER = "dj-neue-version";

export type Anlass = "start" | "anmeldung" | "rueckkehr" | "takt" | "antwort" | "hintergrund" | "warteschlange";

export interface Versionsauskunft {
  version: string;
  dringend: boolean;
  /** Der Dienst nimmt die laufende Version nicht mehr an (älter als `mindestens`) */
  zuAlt: boolean;
}

export interface Lage {
  /** Text wird eingegeben oder ein modaler Dialog ist offen */
  eingabeOderDialog: boolean;
  /** Ein Befehl der Warteschlange ist unterwegs */
  uebertraegt: boolean;
  /** Millisekunden seit Start bzw. letzter Rückkehr */
  seitRueckkehrMs: number;
  /** Millisekunden Gerätezeit seit der letzten Bedienung */
  seitBedienungMs: number;
  jagdtag: boolean;
}

/** Darf die App jetzt auf die Version `a` wechseln? Reine Regel von ADR-0041 §3–§5 (Unit-Tests: web/test). */
export function jetztUebernehmen(a: Versionsauskunft, anlass: Anlass, l: Lage): boolean {
  if (a.zuAlt) return !l.uebertraegt;
  if (l.eingabeOderDialog || l.uebertraegt) return false;
  const sichererZeitpunkt = anlass === "hintergrund" || l.seitRueckkehrMs < FRISCH_MS || l.seitBedienungMs >= RUHE_MS;
  if (!sichererZeitpunkt) return false;
  return a.dringend || !l.jagdtag;
}

/** Spur der Entscheidungen in der Konsole des Geräts (Fehlersuche auf Telefonen über die Entwicklerwerkzeuge). */
function spur(text: string, angaben?: unknown): void {
  console.info(`[Lebenszyklus ${VERSION}] ${text}`, angaben ?? "");
}

function workerstand(reg: ServiceWorkerRegistration | null): Record<string, string | null> {
  return { installing: reg?.installing?.state ?? null, waiting: reg?.waiting?.state ?? null, active: reg?.active?.state ?? null,
    controller: navigator.serviceWorker?.controller?.state ?? null };
}

// -- Hinweis ------------------------------------------------------------------------------------------------------------

let hinweis: string | null = null;
let hinweisSeit = 0;
const hinweisHoerer = new Set<() => void>();

function hinweisSetzen(text: string | null): void {
  hinweis = text;
  hinweisSeit = performance.now();
  for (const h of hinweisHoerer) h();
}

/** Hinweis des Lebenszyklus („Neue Version geladen.“, „Neue Version wird geladen.“) oder `null`. */
export function useVersionshinweis(): string | null {
  return useSyncExternalStore((f) => { hinweisHoerer.add(f); return () => hinweisHoerer.delete(f); }, () => hinweis);
}

// -- Zustand ------------------------------------------------------------------------------------------------------------

let registrierung: ServiceWorkerRegistration | null = null;
let ausstehend: Versionsauskunft | null = null;
let workerBereit = false;
let rueckkehrSeit = 0;
let letzteBedienung = 0;
let letztePruefung = 0;
let letzteAntwortpruefung = -Infinity;
let pruefung: Promise<void> | null = null;
let nochmal: Anlass | null = null;
let uebernahme = false;
let angefordert = false;

interface Merker { von: string; ziel: string; versuche: number }

function merkerLesen(): Merker | null {
  try {
    return JSON.parse(sessionStorage.getItem(MERKER) ?? "null") as Merker | null;
  } catch {
    return null;
  }
}

function merkerSchreiben(m: Merker | null): void {
  try {
    if (m) sessionStorage.setItem(MERKER, JSON.stringify(m));
    else sessionStorage.removeItem(MERKER);
  } catch { /* privater Modus: dann ohne Hinweis */ }
}

function eingabeOderDialog(): boolean {
  const aktiv = document.activeElement;
  if (aktiv?.matches("input:not([type=checkbox]):not([type=radio]):not([type=button]), textarea, [contenteditable='true']")) return true;
  return !!document.querySelector("[role='dialog']:not([aria-modal='false']), [role='alertdialog']");
}

function lage(): Lage {
  return {
    eingabeOderDialog: eingabeOderDialog(), uebertraegt: uebertraegt(), seitRueckkehrMs: performance.now() - rueckkehrSeit,
    seitBedienungMs: Date.now() - letzteBedienung, jagdtag: jagdtagLaeuft(),
  };
}

function neuLaden(): void {
  spur("lädt neu", workerstand(registrierung));
  window.location.reload();
}

/** Letzter Ausweg, wenn die Navigation zum neuen Worker hängt: abbrechen, Worker abmelden, aus dem Netz laden. */
async function abmeldenUndNeuLaden(): Promise<void> {
  spur("Navigation hängt: meldet den Worker ab", workerstand(registrierung));
  window.stop();
  await registrierung?.unregister().catch(() => false);
  window.location.reload();
}

function warten(ms: number): Promise<void> {
  return new Promise((weiter) => setTimeout(weiter, Math.max(0, ms)));
}

/** Wartet, bis ein gerade installierender Worker installiert (oder verworfen) ist. */
function installiert(reg: ServiceWorkerRegistration): Promise<void> {
  const w = reg.installing;
  if (!w) return Promise.resolve();
  return new Promise((fertig) => {
    const t = setTimeout(fertig, INSTALLATION_MS);
    w.addEventListener("statechange", () => {
      if (w.state !== "installing") { clearTimeout(t); fertig(); }
    });
  });
}

/** Neuen Worker holen und installieren lassen (er wartet dann, sw.ts). */
async function vorbereiten(): Promise<void> {
  const reg = registrierung;
  if (reg) {
    await reg.update().catch(() => undefined);
    await installiert(reg);
  }
  workerBereit = true;
  spur("neuer Worker bereit", workerstand(reg));
}

async function uebernehmen(a: Versionsauskunft): Promise<void> {
  const m = merkerLesen();
  const versuche = m && m.von === VERSION && m.ziel === a.version ? m.versuche : 0;
  if (versuche >= VERSUCHE_JE_ZIEL) {
    spur(`kein weiterer Versuch auf ${a.version}`);
    return;
  }
  uebernahme = true;
  merkerSchreiben({ von: VERSION, ziel: a.version, versuche: versuche + 1 });
  if (a.zuAlt) await warten(HINWEIS_LADEN_MINDESTENS_MS - (performance.now() - hinweisSeit));
  const wartend = registrierung?.waiting;
  if (wartend) {
    angefordert = true;
    spur(`fordert den Worker für ${a.version} auf zu übernehmen`, workerstand(registrierung));
    wartend.postMessage({ typ: "uebernehmen" });
    setTimeout(() => {
      spur("kein Wechsel des Workers", workerstand(registrierung));
      neuLaden();
      setTimeout(() => void abmeldenUndNeuLaden(), RUECKFALL_ABMELDEN_MS);
    }, RUECKFALL_NEULADEN_MS);
    return;
  }
  neuLaden();
}

function sichtbar(): boolean {
  return document.visibilityState !== "hidden";
}

function versuchen(anlass: Anlass): void {
  const a = ausstehend;
  if (!a || !workerBereit || uebernahme) return;
  // Im Hintergrund ruht die App (iOS hält sie an); übernommen wird dort nur im Moment des Wechsels.
  if (!sichtbar() && anlass !== "hintergrund") return;
  const l = lage();
  const ja = jetztUebernehmen(a, anlass, l);
  spur(`${anlass}: ${ja ? "übernimmt" : "wartet"} (${a.version})`, l);
  if (ja) void uebernehmen(a);
}

async function abfragen(): Promise<Versionsauskunft | null> {
  const kopf = versionskoepfe();
  const token = await idToken().catch(() => null);
  if (token) kopf["Authorization"] = `Bearer ${token}`;
  const r = await fetch("/api/version", { cache: "no-store", headers: kopf }).catch(() => null);
  if (!r?.ok) return null;
  const k = (await r.json().catch(() => ({}))) as { version?: unknown; dringend?: unknown; angenommen?: unknown };
  if (typeof k.version !== "string" || !k.version) return null;
  return { version: k.version, dringend: k.dringend === true, zuAlt: k.angenommen === false };
}

/** Fragt den Dienst nach seiner Version; eine andere wird vorbereitet und an einem sicheren Punkt übernommen. */
export function pruefen(anlass: Anlass): Promise<void> {
  if (!sichtbar() && (anlass === "antwort" || anlass === "takt")) return Promise.resolve();
  if (pruefung) {
    nochmal = anlass;
    return pruefung;
  }
  pruefung = (async () => {
    try {
      if (!navigator.onLine) return;
      letztePruefung = Date.now();
      const a = await abfragen();
      if (!a) return;
      if (a.version === VERSION) {
        ausstehend = null;
        return;
      }
      const neu = ausstehend?.version !== a.version;
      ausstehend = a;
      if (a.zuAlt && hinweis !== WORTLAUT_NEUE_VERSION_WIRD_GELADEN) hinweisSetzen(WORTLAUT_NEUE_VERSION_WIRD_GELADEN);
      if (neu || !workerBereit) {
        workerBereit = false;
        await vorbereiten();
      }
      versuchen(anlass);
    } finally {
      pruefung = null;
      const weiter = nochmal;
      nochmal = null;
      if (weiter) void pruefen(weiter);
    }
  })();
  return pruefung;
}

function rueckkehr(): void {
  rueckkehrSeit = performance.now();
  void pruefen("rueckkehr");
}

/**
 * Startet den Lebenszyklus nach der Registrierung des Service Workers (main.tsx). Nach einem Wechsel der Version zeigt
 * die App den Hinweis „Neue Version geladen.“ zehn Sekunden lang.
 */
export function lebenszyklusStarten(reg: ServiceWorkerRegistration | null): void {
  registrierung = reg;
  rueckkehrSeit = performance.now();
  letzteBedienung = Date.now();
  const m = merkerLesen();
  if (m && m.von !== VERSION) {
    merkerSchreiben(null);
    hinweisSetzen(WORTLAUT_NEUE_VERSION_GELADEN);
    // Die zehn Sekunden laufen ab dem Moment, in dem die App sichtbar ist (neu geladen auch im Hintergrund).
    const ausblenden = () => setTimeout(() => { if (hinweis === WORTLAUT_NEUE_VERSION_GELADEN) hinweisSetzen(null); }, HINWEIS_GELADEN_MS);
    if (sichtbar()) ausblenden();
    else {
      const beimZeigen = () => {
        if (!sichtbar()) return;
        document.removeEventListener("visibilitychange", beimZeigen);
        ausblenden();
      };
      document.addEventListener("visibilitychange", beimZeigen);
    }
  }

  if ("serviceWorker" in navigator) {
    let controller = navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      const vorher = controller;
      controller = navigator.serviceWorker.controller;
      spur("Worker gewechselt", { angefordert, vorher: !!vorher, ...workerstand(registrierung) });
      if (angefordert) { neuLaden(); return; }
      // Erstinstallation (der Worker übernimmt eine Seite ohne Worker) bzw. ein Ereignis ohne Wechsel: keine neue Version.
      if (!vorher || vorher === controller) return;
      // Ein anderes Fenster derselben App hat den neuen Worker übernehmen lassen: hier an einem sicheren Punkt neu laden.
      ausstehend ??= { version: "?", dringend: false, zuAlt: false };
      workerBereit = true;
      versuchen("antwort");
    });
  }
  document.addEventListener("visibilitychange", () => {
    if (!sichtbar()) versuchen("hintergrund");
    else rueckkehr();
  });
  window.addEventListener("online", rueckkehr);
  for (const art of ["pointerdown", "keydown"]) {
    document.addEventListener(art, () => { letzteBedienung = Date.now(); }, { capture: true, passive: true });
  }
  abweichendeVersionBeobachten((v) => {
    if (v === ausstehend?.version || performance.now() - letzteAntwortpruefung < ANTWORT_ABSTAND_MS) return;
    letzteAntwortpruefung = performance.now();
    void pruefen("antwort");
  });
  warteschlangeBeobachten(() => versuchen("warteschlange"));
  onAuthStateChanged(firebaseDienste().auth, (u) => { if (u) void pruefen("anmeldung"); });
  setInterval(() => {
    if (!sichtbar()) return;
    const jetzt = Date.now();
    if (jetzt < letztePruefung) letztePruefung = jetzt;
    if (jetzt - letztePruefung >= PRUEFTAKT_MS) void pruefen("takt");
    else versuchen("takt");
  }, TAKT_MS);
  void pruefen("start");
}
